<script setup lang="ts">
import dayjs from 'dayjs';
import SocialFeedCard from './parts/SocialFeedCard.vue';
import NoResponseComponent from '@Portal/components/NoResponseComponent.vue';
import { useFilterStore } from '@Portal/stores/filter';
import { storeToRefs } from 'pinia';
import { useSocialStore } from '@Portal/stores/social';
import SkeletonSocialReviewFeed from '@Shared/components/visuals/skeletons/SocialReviewFeed.vue';
import { NoResponsesVariationType } from '@Portal/models/NoResponsesVariationType';
import isoWeek from 'dayjs/plugin/isoWeek';
import isToday from 'dayjs/plugin/isToday';

dayjs.extend(isoWeek);
dayjs.extend(isToday);

export interface SocialProcessFlowCardResult {
  id: string;
  name: string;
  location: string;
  rating: number;
  ratingIndex: number;
  isUnread: boolean;
  reviewPostedAt: string;
  reviewComment?: string;
  reviewReply?: string;
  repliedAt?: string;
  isMatched: boolean;
  processFlowLink?: string;
  programName?: string;
  contactTitle?: string;
  surveyRespondedOn?: string;
}

interface SocialReviewFeedViewModel {
  count?: number;
  title?: string;
  pageSize?: number;
  processFlowModuleId?: string;
  profile?: string;
  emptyStateVariation?: string;
  defaultTab?: string;
  cards?: SocialProcessFlowCardResult[];
}

const availableTabs = ['Reviews Generated', 'Unread', 'Awaiting Replies', 'All Reviews'] as const;

const props = defineProps<SocialReviewFeedViewModel>();
const { count, pageSize, processFlowModuleId, profile, emptyStateVariation, defaultTab, cards } = toRefs(props);

const emit = defineEmits<{
  (event: 'loadPage', page: number, mergeResultsKey?: string, feedTab?: string, sortOrder?: string): void;
}>();

const currentTab = ref<string>(defaultTab.value ?? availableTabs[0]);
const currentPage = ref<number>(0);

const socialStore = useSocialStore();

const fetchData = (loadMore: boolean) => {
  emit('loadPage', currentPage.value, loadMore ? 'cards' : undefined, currentTab.value, currentSortOrder.value);
};

const changeTab = (newTab: string) => {
  currentPage.value = 0;
  currentTab.value = newTab;
  fetchData(false);

  socialStore.trackEvent('Tab Changed', {});
};

const loadNextPage = () => {
  currentPage.value++;
  fetchData(true);
  socialStore.trackEvent('Load Next Page', {});
};

const currentSortOrder = ref<string>('NewestToOldest');

const category = (card: SocialProcessFlowCardResult) => {
  if (!card.reviewPostedAt) return 'Older';

  const date = dayjs(card.reviewPostedAt);
  const now = dayjs(import.meta.env.VITE_DATE_ANCHOR);

  if (now.year() == date.year() && now.month() == date.month() && now.day() == date.day()) {
    return 'Today';
  } else if (now.year() == date.year() && now.isoWeek() == date.isoWeek()) {
    return 'This Week';
  } else if (now.year() == date.year() && now.month() == date.month()) {
    return 'This Month';
  } else {
    return 'Older';
  }
};

export interface SocialReviewFeedProcessedCard extends SocialProcessFlowCardResult {
  group?: string;
  callToActionUrl: string;
  callToAction: string;
}

const processedCards = computed<SocialReviewFeedProcessedCard[]>(() => {
  const result: SocialReviewFeedProcessedCard[] = [];
  if (!cards.value) return result;

  let previousCardCategory = '';
  cards.value.forEach((card) => {
    const cardCategory = category(card);
    let hasNewCardCategory = false;
    if (previousCardCategory !== cardCategory) {
      previousCardCategory = cardCategory;
      hasNewCardCategory = true;
    }

    const baseUrl = location.origin + '/processflow/' + processFlowModuleId.value + '/' + card.id + '/' + profile.value;
    let callToAction = '';
    let callToActionUrl = '';
    // A bit hard coded and should be generalised in the future when we do visuals like frontline dashboard
    // but given it's just for SocialCX at the moment this covers our usecases cleanly enough.
    if (card.repliedAt) {
      callToAction = 'View Details';
      callToActionUrl = baseUrl;
    } else if (card.ratingIndex > 0) {
      callToAction = 'Say Thanks';
      callToActionUrl = baseUrl + '?action=Reply&quickTemplate=Thank%20you';
    } else if (card.ratingIndex == 0) {
      callToAction = 'Reply';
      callToActionUrl = baseUrl + '?action=Reply&quickTemplate=Generic%20reply';
    } else {
      // card.ratingIndex < 0, has a different url but a generic CTA
      callToAction = 'Reply';
      callToActionUrl = baseUrl + '?action=Reply&quickTemplate=Sorry%20this%20happened';
    }

    result.push({
      ...card,
      group: hasNewCardCategory ? cardCategory : undefined,
      callToAction,
      callToActionUrl,
    });
  });

  return result;
});

const pageCount = computed<number>(() => (count.value && pageSize.value ? Math.ceil(count.value / pageSize.value) : 0));
const currentEmptyStateVariation = computed<NoResponsesVariationType | null>(
  () =>
    (emptyStateVariation.value
      ? 'SocialCX-' + emptyStateVariation.value
      : 'SocialCX-NoReviews') as NoResponsesVariationType
);

const filterStore = useFilterStore();
const { dateFilterDisplayValue } = storeToRefs(filterStore);
</script>

<template>
  <SkeletonSocialReviewFeed v-if="!cards" />
  <div class="review-feed stretch-height" v-else>
    <div class="visual-title">Reviews {{ dateFilterDisplayValue }}</div>
    <div class="review-navigation">
      <a
        v-for="tab in availableTabs"
        :key="tab"
        @click="changeTab(tab)"
        :class="{ active: tab == currentTab, 'disabled-waiting': !cards }"
        class="review-nav-item"
      >
        {{ tab }}
      </a>
    </div>

    <div v-if="cards && cards.length > 0">
      <div v-for="card in processedCards" :key="card.id">
        <div v-if="card.group" class="review-card-grouping">{{ card.group }}</div>
        <SocialFeedCard :card="card" />
      </div>
      <div
        class="review-pagination"
        :class="{ disabled: currentPage + 1 >= pageCount }"
        @click="loadNextPage()"
        v-if="pageCount > 0"
      >
        Load More
      </div>
    </div>
    <NoResponseComponent
      v-else-if="currentEmptyStateVariation"
      :variation="currentEmptyStateVariation"
      :special-action="() => changeTab('Awaiting Replies')"
    ></NoResponseComponent>
  </div>
</template>

<style scoped lang="scss">
.review-feed {
  .review-navigation {
    display: flex;
    flex-wrap: wrap;
    padding: 16px;

    .review-nav-item {
      font-weight: 600;
      font-size: 16px;
      // subtract the padding from the 42px margin (since the line extends)
      margin-right: 35px;
      // Make the line longer
      padding-left: 7px;
      padding-right: 7px;
      cursor: pointer;
      color: #4a4a4a;

      display: block;

      &:hover,
      &.active {
        color: #c73362;
        text-decoration: none;
      }

      &.active {
        border-bottom: 2px solid #c73362;
      }

      &.disabled-waiting {
        cursor: wait;
      }
    }
  }

  .review-pagination {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: #4a4a4a;
    margin-bottom: 48px;
    margin-top: 48px;
    font-size: 16px;
    line-height: 22px;
    cursor: pointer;
    text-decoration: underline;

    &.disabled {
      display: none;
    }

    &:hover {
      color: #c73362;
    }
  }

  .review-card-grouping {
    background: #e0e0e0;
    width: 100%;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    color: #4a4a4a;
    padding: 8px 16px;
    text-transform: uppercase;
  }

  @media only screen and (max-width: 480px) {
    .review-navigation .review-nav-item {
      margin-right: 25px;
      padding-left: 7px;
      padding-right: 7px;
    }
  }

  @media only screen and (max-width: 448px) {
    .review-pagination {
      .review-pagination-button {
        font-size: 12px;

        svg {
          vertical-align: middle;
        }
      }

      .review-page-indicator {
        font-size: 12px;
        margin-left: 20px;
        margin-right: 20px;
      }
    }

    .review-navigation .review-nav-item {
      font-size: 12px;
      margin-right: 11px;
      padding-left: 4px;
      padding-right: 4px;
    }

    .review-card-grouping {
      font-size: 10px;
    }
  }

  #no-data-page {
    // Hacks to fix padding for no-data in social-cx
    // should hopefully be replaced with a better solution in the future
    // when we do more cleanup on the no-data visuals.
    @media only screen and (max-width: 768px) {
      padding-top: 50px !important;
    }

    @media only screen and (max-width: 480px) {
      padding-top: 25px !important;
    }
  }
}
</style>
